import { Checkbox, Link, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import React from 'react';
import { useOutletContext } from 'react-router-dom';

import { TabPaper } from '@/components/atoms/TabPaper';
import { LoadingButton } from '@/components/molecules/LoadingButton';
import { NotificationsPreferences, UserPreferencesContextType } from '@/DTOs/userPreferences';
import { useTranslator } from '@/hooks/useTranslator';

const checkIfIsAllSelected = (
  transferStatus: NotificationsPreferences['transferStatus'],
): boolean => {
  return (
    transferStatus.created &&
    transferStatus.paymentSent &&
    transferStatus.completed &&
    transferStatus.failed
  );
};

export const SettingsNotificationsTab: React.FC = (): React.ReactElement => {
  const context = useOutletContext<UserPreferencesContextType>();
  const { preferences, loading, update } = context;

  const [localSettings, setLocalSettings] = React.useState<NotificationsPreferences>(
    preferences.notifications,
  );

  const tr = useTranslator();

  const isAllSelected = React.useMemo((): boolean => {
    return checkIfIsAllSelected(localSettings.transferStatus);
  }, [localSettings.transferStatus]);

  const onSelectAll = React.useCallback((): void => {
    setLocalSettings((oldSettings: NotificationsPreferences): NotificationsPreferences => {
      const isAllSelected = checkIfIsAllSelected(oldSettings.transferStatus);
      const shouldSelectAll = !isAllSelected;

      return {
        ...oldSettings,
        transferStatus: {
          created: shouldSelectAll,
          paymentSent: shouldSelectAll,
          completed: shouldSelectAll,
          failed: shouldSelectAll,
        },
      };
    });
  }, []);

  const handleSave = React.useCallback(async (): Promise<void> => {
    await update({ notifications: localSettings });
  }, [localSettings, update]);

  const onValueChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, checked } = event.target;
    const [group, key] = name.split('.') as [
      keyof NotificationsPreferences,
      keyof NotificationsPreferences[keyof NotificationsPreferences],
    ];

    setLocalSettings((oldSettings: NotificationsPreferences): NotificationsPreferences => {
      const newSettings: Partial<NotificationsPreferences> = {
        [group]: {
          ...oldSettings[group],
          [key]: checked,
        },
      };

      return {
        ...oldSettings,
        ...newSettings,
      };
    });
  }, []);

  const renderItem = React.useCallback(
    (item: NotificationsSettingEntry): React.ReactElement => {
      const key = `${item.group}.${item.key}`;

      return (
        <Box key={key} display="flex" justifyContent="space-between">
          <Box>
            <Typography fontSize={16} lineHeight="24px">
              {tr(item.label)}
            </Typography>
            <Typography fontSize={16} color="custom.LightGray" lineHeight="24px">
              {tr(item.description)}
            </Typography>
          </Box>
          <Box flexShrink={0}>
            <Checkbox
              name={key}
              checked={getBooleanValue(localSettings, item)}
              onChange={onValueChange}
            />
          </Box>
        </Box>
      );
    },
    [localSettings, onValueChange, tr],
  );

  return (
    <TabPaper>
      <Box display="flex" flexDirection="column" gap={5}>
        <Box display="flex" flexDirection="column" gap={4}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h7">{tr('TRANSFER_STATUS')}</Typography>
            <Link
              component="button"
              fontFamily="Inter"
              fontWeight={600}
              color="custom.DefaultText"
              underline="always"
              onClick={onSelectAll}>
              {tr(isAllSelected ? 'DESELECT_ALL' : 'SELECT_ALL')}
            </Link>
          </Box>
          {transferNotifications.map(renderItem)}
        </Box>
        <Box>
          <LoadingButton variant="dark" loading={loading} onClick={handleSave}>
            {tr('SAVE_CHANGES')}
          </LoadingButton>
        </Box>
      </Box>
    </TabPaper>
  );
};

interface TransferStatusNotificationsEntry {
  readonly label: string;
  readonly description: string;
  readonly group: 'account';
  readonly key: 'updateEmailAddress' | 'changePassword' | 'newDeviceLogin';
}

interface AccountNotificationsEntry {
  readonly label: string;
  readonly description: string;
  readonly group: 'transferStatus';
  readonly key: 'created' | 'paymentSent' | 'completed' | 'failed';
}

type NotificationsSettingEntry = TransferStatusNotificationsEntry | AccountNotificationsEntry;

const transferNotifications: readonly NotificationsSettingEntry[] = [
  {
    label: 'TRANSFER_CREATED_LABEL',
    description: 'TRANSFER_CREATED_DESCRIPTION',
    group: 'transferStatus',
    key: 'created',
  },
  {
    label: 'PAYMENT_SENT_LABEL',
    description: 'PAYMENT_SENT_DESCRIPTION',
    group: 'transferStatus',
    key: 'paymentSent',
  },
  {
    label: 'COMPLETED_LABEL',
    description: 'COMPLETED_DESCRIPTION',
    group: 'transferStatus',
    key: 'completed',
  },
  {
    label: 'FAILED_LABEL',
    description: 'FAILED_DESCRIPTION',
    group: 'transferStatus',
    key: 'failed',
  },
];

const getBooleanValue = (
  settings: Partial<NotificationsPreferences>,
  item: NotificationsSettingEntry,
): boolean => {
  switch (item.group) {
    case 'transferStatus':
      switch (item.key) {
        case 'created':
        case 'paymentSent':
        case 'completed':
        case 'failed':
          return settings[item.group]?.[item.key] ?? false;
        default:
          return false;
      }
    default:
      return false;
  }
};
